import { AlertCircle, Expand, Pencil, Plus, UploadCloud, X } from "lucide-react";
import { type FormEvent, useRef, useState } from "react";
import { toast } from "sonner";

import { Avatar } from "@/components/avatar";
import { PictureChooser } from "@/components/avatar-picker";
import { FilePreview } from "@/components/file-preview";
import { Button, IconButton } from "@/components/ui/button";
import { Modal } from "@/components/ui/dialog";
import { Field, Form, Input, Label, Textarea } from "@/components/ui/field";
import {
  PhoneInput,
  emptyPhone,
  hasNumber,
  type PhoneValue,
} from "@/components/ui/phone-input";
import { DEFAULT_COUNTRY, findCountry, phoneProblem } from "@/constants/countries";
import { errorMessage } from "@/services/api";
import { useUpdateProfile, type ProfileUpdateInput } from "@/services/queries";
import { useTranslation } from "@/store/locale";
import type { Profile } from "@/meta/types";
import { cn } from "@/utils/utils";

/** The note's ceiling, as the column holds it. */
const NOTES_MAX = 500;
/** As many further numbers as the API will take. */
const CONTACTS_MAX = 10;

/**
 * A name typed as one line, split into the three columns that hold it.
 *
 * The sheet asks for a full name because that is what a person has; the
 * account has a given name, a family name and whatever is between them. One
 * word is a first name and nothing else — `last_name` is nullable precisely so
 * a person with a single name is recordable — two are first and last, and
 * anything more puts the middle of the line in the middle column.
 *
 * It round-trips what it is shown: the API composes `full_name` by joining the
 * three back in the same order.
 */
function splitName(full: string): {
  first_name: string;
  middle_name: string;
  last_name: string;
} {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  const first = parts.shift() ?? "";
  const last = parts.length > 0 ? (parts.pop() ?? "") : "";
  // Empty rather than null for the two that may be absent: this is a patch,
  // where null means "not sent" and would leave yesterday's family name on an
  // account whose owner has just dropped it. `''` is what clears a field here.
  return { first_name: first, middle_name: parts.join(" "), last_name: last };
}

/** The sheet's own state, filled from the account as it stands. */
function initial(profile: Profile) {
  const country = profile.phone_country ?? DEFAULT_COUNTRY;
  return {
    // A draft like everything else on this sheet: nothing about the picture
    // reaches the server until Update Profile, so closing the sheet puts back
    // whatever was on file. Null is "no picture", which is a state somebody
    // can choose here; it is not the same as "not touched".
    avatar: profile.avatar,
    fullName: profile.full_name,
    notes: profile.notes ?? "",
    phone: profile.phone ? { country, phone: profile.phone } : emptyPhone(country),
    contacts:
      profile.contacts.length > 0
        ? profile.contacts.map((contact) => ({
            country: contact.country ?? DEFAULT_COUNTRY,
            phone: contact.phone,
          }))
        : // One empty row, so the field the label names is on screen. Adding
          // the first number should not cost a press of + first. It starts on
          // the Philippines rather than following the main number: a second
          // number somebody adds here is the local mobile, whatever country
          // the line at the top of the form is in.
          [emptyPhone(DEFAULT_COUNTRY)],
  };
}

/** The contacts on a profile, as one comparable string. */
const sameContacts = (a: ProfileUpdateInput["contacts"], b: Profile["contacts"]) =>
  JSON.stringify(a) === JSON.stringify(b.map(({ phone, country }) => ({ phone, country })));

/**
 * What the person actually changed — and nothing else.
 *
 * This is what a PATCH is, and sending the whole sheet every time is not
 * merely wasteful. A field that is unchanged and sent anyway still has to pass
 * validation, so an account with no middle name was sending `middle_name: ""`
 * on every save and being refused by a column that requires a character —
 * refused, that is, for not changing something. It also means two people
 * editing different halves of their own profile never write over each other's
 * halves, and the picture only travels when it is the picture that moved.
 */
function changes(profile: Profile, form: ReturnType<typeof initial>): ProfileUpdateInput {
  const body: ProfileUpdateInput = {};

  const names = splitName(form.fullName);
  if (names.first_name !== profile.first_name) body.first_name = names.first_name;
  if (names.middle_name !== (profile.middle_name ?? "")) body.middle_name = names.middle_name;
  if (names.last_name !== (profile.last_name ?? "")) body.last_name = names.last_name;

  const notes = form.notes.trim();
  if (notes !== (profile.notes ?? "")) body.notes = notes;

  // The number and the flag travel together: a country on its own says which
  // flag to draw beside a number that is not there. Changing only the flag on
  // an empty field is not a change worth a request.
  const phone = hasNumber(form.phone) ? form.phone.phone : "";
  if (phone !== (profile.phone ?? "")) {
    body.phone = phone;
    body.phone_country = form.phone.country;
  } else if (phone && form.phone.country !== profile.phone_country) {
    body.phone_country = form.phone.country;
  }

  // Rows nobody typed in are the + button pressed and then thought better of —
  // a dialling code on its own is not a number.
  const contacts = form.contacts
    .filter(hasNumber)
    .map((contact) => ({ phone: contact.phone, country: contact.country }));
  if (!sameContacts(contacts, profile.contacts)) body.contacts = contacts;

  // A data URL is the largest thing this request can carry; re-sending an
  // unchanged picture on every save of a note would put kilobytes on the wire
  // for nothing. `''` is what removes one — null would mean "not sent".
  if (form.avatar !== profile.avatar) body.avatar = form.avatar ?? "";

  return body;
}

/**
 * Edit Profile, as the dashboard's own sheet.
 *
 * Legacy opened this from the profile card's ellipsis and it is the whole of
 * what a person may change about themselves: the picture, the name, the note,
 * and the numbers. The email is shown and not editable — it is what a password
 * reset is sent to, and the account's own screens are where that is changed.
 *
 * The picture saves the moment it is chosen, which is deliberate: it goes
 * through a crop stage that has its own Save, and asking for a second one down
 * here would be the same picture saved twice. Everything else waits for Update
 * Profile, so Cancel leaves the form where it was found.
 */
export function EditProfileDialog({
  profile,
  open,
  onClose,
}: {
  profile: Profile;
  open: boolean;
  onClose: () => void;
}) {
  const t = useTranslation();
  const update = useUpdateProfile();
  // Read once, on the way in. The profile changes under this while it is open
  // — the picture saves on its own — and re-deriving from it would take back
  // the name and note the person is part-way through typing. The caller mounts
  // the sheet only while it is open, so each visit starts from the record.
  const [form, setForm] = useState(() => initial(profile));
  const [error, setError] = useState<string | null>(null);
  // Field-level complaints hold until Update Profile is pressed. A number is
  // too short for most of the time it is being typed, and a field that goes
  // red on the first digit is a field that is red at you rather than for you.
  const [showErrors, setShowErrors] = useState(false);

  const name = profile.full_name || profile.user_name;
  const remaining = NOTES_MAX - form.notes.length;

  const setContact = (index: number, value: PhoneValue) =>
    setForm((was) => ({
      ...was,
      contacts: was.contacts.map((contact, at) => (at === index ? value : contact)),
    }));

  const addContact = () =>
    setForm((was) => ({
      ...was,
      contacts: [...was.contacts, emptyPhone(DEFAULT_COUNTRY)],
    }));

  const removeContact = (index: number) =>
    setForm((was) => {
      const kept = was.contacts.filter((_, at) => at !== index);
      // Never down to nothing: the label would then name a field that is not
      // there, and the only way back would be a + with no row beside it.
      return {
        ...was,
        contacts: kept.length > 0 ? kept : [emptyPhone(DEFAULT_COUNTRY)],
      };
    });

  /** What is wrong with a number, in words, or undefined when nothing is. */
  const numberError = (value: PhoneValue): string | undefined => {
    const problem = phoneProblem(findCountry(value.country), value.phone);
    if (!problem) return undefined;
    const { min, max } = problem;
    const howMany =
      min === max
        ? t("profile.phoneDigits").replace("{min}", String(min))
        : t("profile.phoneDigitsRange")
            .replace("{min}", String(min))
            .replace("{max}", String(max));
    return `${t(problem.kind === "short" ? "profile.phoneShort" : "profile.phoneLong")} ${howMany}`;
  };

  const phoneError = numberError(form.phone);
  // Only rows somebody actually typed in: an untouched row is dropped on save,
  // so complaining that its dialling code is too short would be complaining
  // about a field nobody filled in.
  const contactErrors = form.contacts.map((contact) =>
    hasNumber(contact) ? numberError(contact) : undefined,
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!form.fullName.trim()) {
      setError(t("profile.errorName"));
      return;
    }
    // The messages are already under the fields they belong to; the banner
    // would only repeat them further from the thing to fix.
    if (phoneError || contactErrors.some(Boolean)) {
      setError(null);
      setShowErrors(true);
      return;
    }
    setError(null);
    setShowErrors(false);

    const body = changes(profile, form);
    // Nothing moved. A patch of no fields is a round trip to be told what we
    // already know, so the sheet just closes.
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }

    update.mutate(body, {
      onSuccess: () => {
        toast.success(t("profile.updated"));
        onClose();
      },
      onError: (cause) => setError(errorMessage(cause, t("profile.saveFailed"))),
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t("shell.editProfile")}
      titleDivider
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            {t("profile.cancel")}
          </Button>
          <Button
            type="submit"
            form="edit-profile"
            loading={update.isPending}
            loadingLabel={t("profile.saving")}
          >
            {t("profile.updateProfile")}
          </Button>
        </>
      }
    >
      {/* The submit button lives in the sheet's footer, outside this element,
          and reaches the form by `form=` — which is what that attribute is for
          and what keeps Enter in any field submitting the same thing. */}
      <Form id="edit-profile" onSubmit={submit} noValidate className="grid gap-5 py-4">
        <PictureWell
          name={name}
          value={form.avatar}
          busy={update.isPending}
          onChange={(avatar) => setForm((was) => ({ ...was, avatar }))}
        />

        <Field
          label={t("profile.fullName")}
          htmlFor="profile-name"
          required
          error={error && !form.fullName.trim() ? error : undefined}
        >
          <Input
            id="profile-name"
            autoComplete="name"
            value={form.fullName}
            aria-invalid={Boolean(error && !form.fullName.trim())}
            onChange={(event) => setForm((was) => ({ ...was, fullName: event.target.value }))}
          />
        </Field>

        <Field label={t("profile.email")} htmlFor="profile-email" hint={t("profile.emailHint")}>
          <Input
            id="profile-email"
            type="email"
            value={profile.email_address ?? ""}
            placeholder={t("profile.noEmail")}
            disabled
            readOnly
          />
        </Field>

        <Field
          label={t("profile.notes")}
          htmlFor="profile-notes"
          // The count is the hint slot rather than a line of its own: a field
          // says one thing at a time, and while there is nothing wrong with
          // the note, how much room is left is that thing.
          hint={t("profile.notesRemaining").replace("{n}", String(remaining))}
        >
          <Textarea
            id="profile-notes"
            rows={3}
            maxLength={NOTES_MAX}
            placeholder={t("profile.notesPlaceholder")}
            value={form.notes}
            onChange={(event) => setForm((was) => ({ ...was, notes: event.target.value }))}
          />
        </Field>

        <Field
          label={t("profile.phone")}
          htmlFor="profile-phone"
          error={showErrors ? phoneError : undefined}
        >
          <PhoneInput
            id="profile-phone"
            value={form.phone}
            invalid={showErrors && Boolean(phoneError)}
            placeholder={t("profile.phonePlaceholder")}
            onChange={(phone) => setForm((was) => ({ ...was, phone }))}
          />
        </Field>

        <div className="grid gap-1.5">
          <Label htmlFor="profile-contact-0">
            {t("profile.contacts")}{" "}
            <i className="text-fg-subtle text-footnote font-normal">
              {t("profile.contactsAdditional")}
            </i>
          </Label>
          <div className="grid gap-2">
            {form.contacts.map((contact, index) => {
              const last = index === form.contacts.length - 1;
              return (
                <div key={index} className="flex items-start gap-2">
                  <div className="grid min-w-0 flex-1 gap-1.5">
                    <PhoneInput
                      id={`profile-contact-${index}`}
                      value={contact}
                      invalid={showErrors && Boolean(contactErrors[index])}
                      placeholder={t("profile.contactPlaceholder")}
                      aria-label={t("profile.contactNumber").replace("{n}", String(index + 1))}
                      onChange={(value) => setContact(index, value)}
                    />
                    {showErrors && contactErrors[index] ? (
                      <p
                        role="alert"
                        className="text-danger text-subhead flex items-start gap-1.5"
                      >
                        <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden />
                        <span>{contactErrors[index]}</span>
                      </p>
                    ) : null}
                  </div>
                  {/* The + is on the last row only — one way to add, at the
                      bottom of the list, as legacy had it. Every row above it
                      gets the × instead, so a row is added and taken away from
                      the same place in the line. */}
                  {last ? (
                    <IconButton
                      label={t("profile.addContact")}
                      variant="secondary"
                      size="icon"
                      className="size-10 shrink-0"
                      disabled={form.contacts.length >= CONTACTS_MAX}
                      onClick={addContact}
                    >
                      <Plus aria-hidden />
                    </IconButton>
                  ) : (
                    <IconButton
                      label={t("profile.removeContact").replace("{n}", String(index + 1))}
                      variant="ghost"
                      size="icon"
                      className="size-10 shrink-0"
                      onClick={() => removeContact(index)}
                    >
                      <X aria-hidden />
                    </IconButton>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Anything the server refused, once, above the actions rather than
            pinned to a field that may not be the one at fault. */}
        {error && form.fullName.trim() ? (
          <p
            role="alert"
            className="bg-danger-soft text-danger text-callout rounded-lg px-3 py-2.5"
          >
            {error}
          </p>
        ) : null}
      </Form>
    </Modal>
  );
}

/**
 * The picture, in a well of its own — a draft, like every other field here.
 *
 * Nothing in this component reaches the server. Choosing a picture, or taking
 * one away, changes what the sheet is holding; Update Profile writes it and
 * Cancel forgets it. That is the whole point of the arrangement: somebody who
 * removes their photograph, thinks better of it and closes the sheet still has
 * their photograph.
 *
 * Three controls, three jobs. The picture itself opens the picture — pressing
 * a photograph should show you the photograph, not a file dialog. The pencil
 * replaces it. The × takes it away. With nothing on file the well is a
 * dropzone instead, which is the one state where "press anywhere" is right.
 */
function PictureWell({
  value,
  name,
  busy,
  onChange,
}: {
  /** The picture the sheet is holding: a data URL, or null for none. */
  value: string | null;
  name: string;
  busy: boolean;
  onChange: (value: string | null) => void;
}) {
  const t = useTranslation();
  const [viewing, setViewing] = useState(false);
  const [choosing, setChoosing] = useState(false);
  /** A file dropped on the well, handed straight to the crop stage. */
  const [dropped, setDropped] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const readFile = (chosen: File | undefined) => {
    if (!chosen) return;
    if (!chosen.type.startsWith("image/")) {
      toast.error(t("profile.notAnImage"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setDropped(String(reader.result));
      setChoosing(true);
    };
    reader.onerror = () => toast.error(t("profile.unreadableImage"));
    reader.readAsDataURL(chosen);
  };

  const closeChooser = () => {
    setChoosing(false);
    setDropped(null);
  };

  // One handler for both ways in, because a drop and a press should land in
  // the same place: the crop stage, with the file already in it.
  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (busy) return;
    readFile(event.dataTransfer.files?.[0]);
  };

  const well = cn(
    "relative rounded-xl border border-dashed transition-colors",
    "bg-surface-sunken/40 px-4 py-6",
    dragging ? "border-accent bg-accent-soft/40" : "border-line-strong",
  );

  if (!value) {
    return (
      <>
        {/* Empty: the whole well is the way in. A dashed frame with a picture
            in it is a slot that is filled; the same frame with nothing in it
            should say what to do about that. */}
        <button
          type="button"
          disabled={busy}
          onClick={() => file.current?.click()}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            well,
            "hover:border-accent focus-visible:outline-accent grid w-full place-items-center gap-1.5 py-10",
            "focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-60",
          )}
        >
          <UploadCloud className="text-fg-subtle size-6" aria-hidden />
          <span className="text-callout text-fg-muted">
            <span className="text-heading font-semibold">{t("profile.clickToUpload")}</span>{" "}
            {t("profile.orDragAndDrop")}
          </span>
        </button>
        <input
          ref={file}
          type="file"
          accept="image/*"
          className="sr-only"
          onChange={(event) => {
            readFile(event.target.files?.[0]);
            event.target.value = "";
          }}
        />
        {choosing ? (
          <PictureChooser
            current={null}
            initialSource={dropped}
            onClose={closeChooser}
            onDone={(dataUrl) => {
              closeChooser();
              onChange(dataUrl);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <div
      className={cn(well, "grid place-items-center")}
      onDragOver={(event) => {
        event.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
    >
      <IconButton
        label={t("profile.removePicture")}
        size="icon-sm"
        className="absolute top-2 right-2"
        disabled={busy}
        onClick={() => onChange(null)}
      >
        <X aria-hidden />
      </IconButton>

      <div className="relative">
        <button
          type="button"
          onClick={() => setViewing(true)}
          title={t("profile.viewPicture")}
          className="group focus-visible:outline-accent block rounded-full focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          <Avatar
            src={value}
            name={name}
            placeholder="person"
            className="size-[130px] rounded-full"
          />
          <span
            aria-hidden
            className={cn(
              "bg-ink-900/45 pointer-events-none absolute inset-0 grid place-items-center rounded-full text-white",
              "opacity-0 transition-opacity duration-[var(--duration-fast)]",
              "group-hover:opacity-100 group-focus-visible:opacity-100",
            )}
          >
            <Expand className="size-7" />
          </span>
          <span className="sr-only">{t("profile.viewPicture")}</span>
        </button>

        {/* The pencil is a real button, not the badge it looks like: it is the
            one way to replace the picture, and the picture underneath it has
            been given a job of its own. */}
        <IconButton
          label={t("profile.changePicture")}
          size="icon-sm"
          variant="secondary"
          disabled={busy}
          onClick={() => setChoosing(true)}
          className="absolute right-0 bottom-0 size-8 rounded-full"
        >
          <Pencil className="size-3.5" aria-hidden />
        </IconButton>
      </div>

      {viewing ? (
        <FilePreview
          src={value}
          title={t("shell.profilePicture")}
          size="md"
          onClose={() => setViewing(false)}
          // The picture is looked at here and replaced from here: the pencil
          // beside it does the same thing, and somebody who has just looked at
          // what is on file is exactly who decides to change it.
          actions={
            <Button
              onClick={() => {
                setViewing(false);
                setChoosing(true);
              }}
            >
              <Pencil aria-hidden /> {t("profile.changePicture")}
            </Button>
          }
        />
      ) : null}

      {choosing ? (
        <PictureChooser
          current={value}
          initialSource={dropped}
          onClose={closeChooser}
          onRemove={() => {
            closeChooser();
            onChange(null);
          }}
          onDone={(dataUrl) => {
            closeChooser();
            onChange(dataUrl);
          }}
        />
      ) : null}
    </div>
  );
}
